// Resellers.jsx - Reseller management (migration v5.0)
// A reseller logs in to the reseller portal and sees its clients' stats.
// Its branding (logo, colours, theme, layout) applies to its own portal AND to
// the client portal of every client assigned to it (Clients page → Reseller).
// Backend: routes/reseller-admin.js.

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Search, Edit, Trash2, X, CheckCircle, AlertCircle, Briefcase, RefreshCw, Users, Bot, Loader2
} from 'lucide-react'
import { resellerAdminApi } from '../services/api'
import ClientBrandingFields, { brandingDefaults, pickBranding, uploadPendingBranding, LAYOUTS } from '../components/ClientBrandingFields'
import './Clients.css'

const EMPTY = { name: '', company: '', email: '', phone: '', username: '', password: '', status: 'active', notes: '' }
const STATUS_CLASS = { active: 'status-active', suspended: 'status-suspended', cancelled: '' }

export default function Resellers() {
  const navigate = useNavigate()
  const [resellers, setResellers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [toast, setToast] = useState(null)
  const [modal, setModal] = useState(null)              // null | { mode: 'add' | 'edit', reseller? }
  const [formData, setFormData] = useState({ ...EMPTY, ...brandingDefaults() })
  const [pendingBranding, setPendingBranding] = useState({})
  const [brandingError, setBrandingError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [detail, setDetail] = useState(null)             // { reseller, clients } for the clients drawer

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const load = async () => {
    try {
      const res = await resellerAdminApi.getResellers()
      setResellers(res.data?.resellers || [])
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to load resellers', 'error')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return resellers.filter(r => !q || [r.name, r.company, r.username, r.email].some(v => (v || '').toLowerCase().includes(q)))
  }, [resellers, search])

  const openModal = (mode, reseller = null) => {
    setFormData(mode === 'edit'
      ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, reseller[k] ?? ''])), password: '', ...brandingDefaults(reseller) }
      : { ...EMPTY, ...brandingDefaults() })
    setPendingBranding({}); setBrandingError(null)
    setModal({ mode, reseller })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const data = { ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, formData[k]])), ...pickBranding(formData) }
    try {
      if (modal.mode === 'add') {
        const res = await resellerAdminApi.createReseller(data)
        const imgErr = await uploadPendingBranding(res.data.reseller_id, pendingBranding, 'reseller')
        const err = res.data.branding_error || imgErr
        showToast(err ? `Reseller created — branding not saved: ${err}` : 'Reseller created', err ? 'error' : 'success')
      } else {
        if (!data.password) delete data.password
        await resellerAdminApi.updateReseller(modal.reseller.id, data)
        const imgErr = await uploadPendingBranding(modal.reseller.id, pendingBranding, 'reseller')
        showToast(imgErr ? `Reseller updated — image not saved: ${imgErr}` : 'Reseller updated', imgErr ? 'error' : 'success')
      }
      setModal(null)
      load()
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save reseller', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (r) => {
    const msg = r.client_count
      ? `Remove reseller "${r.name}"?\n\nIts ${r.client_count} client(s) become direct Redstone clients and go back to their own branding.`
      : `Remove reseller "${r.name}"?`
    if (!window.confirm(msg)) return
    try {
      await resellerAdminApi.deleteReseller(r.id)
      showToast(`Reseller "${r.name}" removed`)
      load()
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to remove reseller', 'error')
    }
  }

  const openClients = async (r) => {
    setDetail({ reseller: r, clients: null })
    try {
      const res = await resellerAdminApi.getReseller(r.id)
      setDetail({ reseller: r, clients: res.data?.clients || [] })
    } catch (_) {
      setDetail({ reseller: r, clients: [] })
    }
  }

  const set = (k) => (e) => setFormData(f => ({ ...f, [k]: e.target.value }))
  const layoutLabel = (key) => LAYOUTS.find(l => l.key === (key || 'classic'))?.label || 'Classic'

  return (
    <div className="clients-page">
      <AnimatePresence>
        {toast && (
          <motion.div className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: -20, x: '-50%' }}>
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Resellers</h1>
          <p className="page-subtitle">Partners who resell our bots. A reseller's branding, theme and layout apply to its portal and its clients' portals. Redstone is the default reseller.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => { setLoading(true); load() }}><RefreshCw size={16} /> Refresh</button>
          <button className="btn btn-primary" onClick={() => openModal('add')}><Plus size={16} /> Add Reseller</button>
        </div>
      </div>

      <div className="tw-mb-3 tw-flex tw-max-w-sm tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-input tw-px-3">
        <Search size={16} className="tw-text-muted" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resellers…"
          className="tw-w-full tw-border-0 tw-bg-transparent tw-py-2 tw-outline-none" />
      </div>

      <div className="table-container clients-table-compact">
        <table className="data-table">
          <thead>
            <tr>
              <th>Reseller</th>
              <th style={{ width: '140px' }}>Login</th>
              <th style={{ width: '170px' }}>Branding</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Clients</th>
              <th style={{ width: '70px', textAlign: 'center' }}>Bots</th>
              <th style={{ width: '90px', textAlign: 'center' }}>Status</th>
              <th style={{ width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="empty-state"><Loader2 size={28} className="spin" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  <Briefcase size={48} />
                  <p>{resellers.length ? 'No resellers match your search' : 'No resellers yet'}</p>
                  {!resellers.length && <button className="btn btn-primary" onClick={() => openModal('add')}><Plus size={18} /> Add First Reseller</button>}
                </td>
              </tr>
            ) : filtered.map(r => (
              <tr key={r.id}>
                <td>
                  <div className="client-info-compact clickable" onClick={() => openModal('edit', r)}>
                    {r.brand_logo
                      ? <img src={`/api/client/branding/file/${r.brand_logo}`} alt="" className="tw-h-7 tw-w-7 tw-rounded tw-object-contain" />
                      : <span className="client-avatar-sm" style={r.brand_primary_color ? { background: r.brand_primary_color } : undefined}>{r.name?.charAt(0).toUpperCase()}</span>}
                    <div className="client-name-col">
                      <span className="client-name">{r.name}{!!r.is_default && <span className="badge badge-primary tw-ml-2 tw-align-middle">Default</span>}</span>
                      {r.company && <span className="client-contact-sub">{r.company}</span>}
                    </div>
                  </div>
                </td>
                <td><span className="tw-font-mono tw-text-xs">{r.username}</span></td>
                <td>
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                    <span className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-sm" style={{ background: r.brand_primary_color || '#dc2626' }} />
                    <span className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-sm" style={{ background: r.brand_accent_color || '#f59e0b' }} />
                    <span className="tw-text-muted">{layoutLabel(r.portal_layout)} · {r.portal_theme || 'auto'}</span>
                  </div>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openClients(r)} title="Show clients"><Users size={14} /> {r.client_count}</button>
                </td>
                <td style={{ textAlign: 'center' }}><span className="tw-inline-flex tw-items-center tw-gap-1"><Bot size={14} className="tw-text-muted" />{r.total_bots}</span></td>
                <td style={{ textAlign: 'center' }}><span className={`status-badge-sm ${STATUS_CLASS[r.status] || ''}`}>{r.status}</span></td>
                <td>
                  <div className="tw-flex tw-gap-1">
                    <button className="btn btn-icon btn-sm" onClick={() => openModal('edit', r)} title="Edit"><Edit size={14} /></button>
                    {r.status !== 'cancelled' && !r.is_default && <button className="btn btn-icon btn-sm" onClick={() => handleDelete(r)} title="Remove"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / edit */}
      <AnimatePresence>
        {modal && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setModal(null)}>
            <motion.div className="modal" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modal.mode === 'add' ? 'Add Reseller' : `Edit Reseller — ${modal.reseller.name}`}</h2>
                <button className="modal-close" onClick={() => setModal(null)}><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {!!modal.reseller?.is_default && (
                    <div className="tw-mb-3 tw-rounded-md tw-border tw-border-solid tw-border-border tw-px-3 tw-py-2 tw-text-xs tw-text-muted">
                      <strong className="tw-text-fg">Default reseller.</strong> Clients saved without a reseller belong to it, and clients of a removed reseller move here.
                      Unlike other resellers, its clients keep their own branding. The branding below only fills in what a client leaves empty, and styles this reseller's own portal.
                      It has no portal password until you set one.
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Name *</label>
                      <input value={formData.name} onChange={set('name')} required placeholder="Reseller name" />
                    </div>
                    <div className="form-group">
                      <label>Company</label>
                      <input value={formData.company} onChange={set('company')} placeholder="Legal / company name" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" value={formData.email} onChange={set('email')} />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input value={formData.phone} onChange={set('phone')} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Portal username *</label>
                      <input value={formData.username} onChange={set('username')} required autoComplete="off" />
                    </div>
                    <div className="form-group">
                      <label>{modal.mode === 'add' ? 'Password *' : 'New password'}</label>
                      <input type="password" value={formData.password} onChange={set('password')} required={modal.mode === 'add'}
                        autoComplete="new-password" placeholder={modal.mode === 'edit' ? 'Leave blank to keep the current one' : ''} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Status</label>
                      <select value={formData.status} onChange={set('status')}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended (portal login blocked)</option>
                        {modal.mode === 'edit' && !modal.reseller?.is_default && <option value="cancelled">Cancelled</option>}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea value={formData.notes} onChange={set('notes')} rows={2} placeholder="Internal notes…" />
                  </div>

                  <ClientBrandingFields
                    owner="reseller"
                    formData={formData}
                    setFormData={setFormData}
                    client={modal.mode === 'edit' ? modal.reseller : null}
                    pending={pendingBranding}
                    setPending={setPendingBranding}
                    onError={setBrandingError}
                  />
                  {brandingError && <div className="tw-mt-2 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{brandingError}</div>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving && <Loader2 size={14} className="spin" />} {modal.mode === 'add' ? 'Create Reseller' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clients of one reseller */}
      <AnimatePresence>
        {detail && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDetail(null)}>
            <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{detail.reseller.name} — clients</h2>
                <button className="modal-close" onClick={() => setDetail(null)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                {!detail.clients ? <Loader2 size={24} className="spin" /> : detail.clients.length === 0 ? (
                  <p className="tw-text-sm tw-text-muted">No clients yet. Assign one from the Clients page (edit client → Reseller).</p>
                ) : (
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {detail.clients.map(c => (
                      <button key={c.id} type="button" onClick={() => navigate(`/clients/${c.id}`)}
                        className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm">
                        <span>{c.name}{c.company && c.company !== c.name ? <span className="tw-text-muted"> · {c.company}</span> : null}</span>
                        <span className={`status-badge-sm ${STATUS_CLASS[c.status] || ''}`}>{c.status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
